import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { api, formatINR, loadScript, formatApiError, useSEO } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export default function Checkout() {
  useSEO("Checkout");
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ razorpay_enabled: false, stripe_enabled: true, cod_enabled: true });
  const [gateway, setGateway] = useState("stripe");
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "",
    address_line1: "", address_line2: "", city: "", district: "", state: "", pincode: "", country: "India",
    address: "",
  });
  const [processing, setProcessing] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);

  const discount = applied ? applied.discount : 0;
  const shipping = (total - discount) >= 4999 ? 0 : (total > 0 ? 199 : 0);
  const grand = Math.max(0, total - discount) + shipping;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal: total });
      setApplied(data);
      toast.success(`Applied: ${data.label}`);
    } catch (err) {
      setApplied(null);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/payments/config").then((r) => {
      setConfig(r.data);
      setGateway(r.data.razorpay_enabled ? "razorpay" : "stripe");
    });
  }, []);

  useEffect(() => {
    if (user) setCustomer((c) => ({ ...c, name: c.name || user.name || "", email: c.email || user.email || "", phone: c.phone || user.phone || "" }));
  }, [user]);

  // Compose a combined single-line address for backend compatibility (admin, email, existing display)
  const composeAddress = (c) => {
    const parts = [c.address_line1, c.address_line2, c.city, c.district, c.state, c.pincode, c.country]
      .map((p) => (p || "").trim()).filter(Boolean);
    return parts.join(", ");
  };

  const setField = (k, v) => setCustomer((c) => {
    const next = { ...c, [k]: v };
    next.address = composeAddress(next);
    return next;
  });

  const payload = () => ({
    items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
    origin_url: window.location.origin,
    gateway,
    customer: { ...customer, address: composeAddress(customer) },
    user_id: user ? user.id : null,
    coupon: applied ? applied.code : null,
  });

  const isAddressComplete = () =>
    customer.address_line1.trim() && customer.city.trim() && customer.district.trim() &&
    customer.state.trim() && /^\d{6}$/.test(customer.pincode.trim());

  // Auto-fill City / District / State from India Post public PIN lookup
  const [pinLookup, setPinLookup] = useState({ loading: false, error: "" });
  const lookedUpPin = React.useRef("");
  useEffect(() => {
    const pin = customer.pincode.trim();
    if (!/^\d{6}$/.test(pin) || lookedUpPin.current === pin) return;
    lookedUpPin.current = pin;
    const ctrl = new AbortController();
    setPinLookup({ loading: true, error: "" });
    fetch(`https://api.postalpincode.in/pincode/${pin}`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((data) => {
        const rec = Array.isArray(data) ? data[0] : null;
        const po = rec && rec.Status === "Success" && rec.PostOffice && rec.PostOffice[0];
        if (!po) {
          setPinLookup({ loading: false, error: "Couldn't verify this PIN Code. Please enter city, district and state manually." });
          return;
        }
        setCustomer((c) => ({
          ...c,
          city: c.city || po.Block || po.District || "",
          district: c.district || po.District || "",
          state: c.state || po.State || "",
          address: composeAddress({
            ...c,
            city: c.city || po.Block || po.District || "",
            district: c.district || po.District || "",
            state: c.state || po.State || "",
          }),
        }));
        setPinLookup({ loading: false, error: "" });
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setPinLookup({ loading: false, error: "" });
      });
    return () => ctrl.abort();
  }, [customer.pincode]);

  const validate = () => {
    if (items.length === 0) { toast.error("Your bag is empty."); return false; }
    if (!customer.name || !customer.email) { toast.error("Please enter your name and email."); return false; }
    return true;
  };

  const payStripe = async () => {
    const { data } = await api.post("/payments/stripe/checkout", payload());
    if (data.checkout_url) window.location.href = data.checkout_url;
    else throw new Error("No checkout URL");
  };

  const payRazorpay = async () => {
    const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!ok) throw new Error("Razorpay SDK failed to load");
    const { data } = await api.post("/payments/razorpay/order", payload());
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency,
        name: "VYRON", description: "Luxury Eyewear", order_id: data.order_id,
        prefill: { name: customer.name, email: customer.email, contact: customer.phone },
        theme: { color: "#000000" },
        handler: async (resp) => {
          try {
            const v = await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clear();
            navigate(`/payment/success?gateway=razorpay&order=${v.data.order_number || ""}`);
            resolve();
          } catch { reject(new Error("Verification failed")); }
        },
        modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
      });
      rzp.open();
    });
  };

  const payCOD = async () => {
    if (!customer.phone) {
      toast.error("Please add your phone number for Cash on Delivery.");
      throw new Error("Missing COD details");
    }
    if (!isAddressComplete()) {
      toast.error("Please complete your shipping address — Address, City, District, State and a valid 6-digit PIN Code are required.");
      throw new Error("Missing COD details");
    }
    const { data } = await api.post("/payments/cod/order", payload());
    clear();
    navigate(`/payment/success?gateway=cod&order=${data.order_number || ""}`);
  };

  const pay = async () => {
    if (!validate()) return;
    setProcessing(true);
    try {
      if (gateway === "razorpay") await payRazorpay();
      else if (gateway === "cod") await payCOD();
      else await payStripe();
    } catch (e) {
      if (e?.message !== "Missing COD details") {
        toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      }
      setProcessing(false);
    }
  };

  const field = "w-full bg-[#F5F5F5] rounded-2xl px-5 py-4 text-sm outline-none focus:ring-1 ring-black transition";

  return (
    <div data-testid="checkout-page" className="pt-28 pb-28">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/collections" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] link-underline mb-8">
          <ArrowLeft size={14} /> Continue shopping
        </Link>
        <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] mb-12">Checkout</h1>

        {items.length === 0 ? (
          <p className="text-muted-foreground" data-testid="checkout-empty">
            Your bag is empty. <Link to="/collections" className="link-underline text-black">Explore the collection.</Link>
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 space-y-10">
              {!user && (
                <div className="bg-[#F5F5F5] rounded-2xl px-6 py-4 text-sm flex items-center justify-between" data-testid="guest-notice">
                  <span className="text-muted-foreground">Checking out as guest.</span>
                  <Link to="/login" className="link-underline text-black uppercase text-xs tracking-widest">Sign in</Link>
                </div>
              )}
              <div>
                <h2 className="font-display text-2xl mb-5">Your details</h2>
                <div className="space-y-3">
                  <input data-testid="checkout-name" placeholder="Full name *" value={customer.name} onChange={(e) => setField("name", e.target.value)} className={field} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input data-testid="checkout-email" type="email" placeholder="Email *" value={customer.email} onChange={(e) => setField("email", e.target.value)} className={field} />
                    <input data-testid="checkout-phone" type="tel" placeholder="Phone *" value={customer.phone} onChange={(e) => setField("phone", e.target.value)} className={field} />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-display text-2xl mb-5">Shipping address</h2>
                <div className="space-y-3">
                  <input data-testid="checkout-address-line1" placeholder="Address Line 1 — House/Flat No., Building, Street *"
                    value={customer.address_line1} onChange={(e) => setField("address_line1", e.target.value)} className={field} autoComplete="address-line1" />
                  <input data-testid="checkout-address-line2" placeholder="Address Line 2 — Area, Locality, Landmark (Optional)"
                    value={customer.address_line2} onChange={(e) => setField("address_line2", e.target.value)} className={field} autoComplete="address-line2" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input data-testid="checkout-city" placeholder="City *" value={customer.city}
                      onChange={(e) => setField("city", e.target.value)} className={field} autoComplete="address-level2" />
                    <input data-testid="checkout-district" placeholder="District *" value={customer.district}
                      onChange={(e) => setField("district", e.target.value)} className={field} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input data-testid="checkout-state" placeholder="State *" value={customer.state}
                      onChange={(e) => setField("state", e.target.value)} className={`${field} md:col-span-1`} autoComplete="address-level1" />
                    <input data-testid="checkout-pincode" inputMode="numeric" maxLength={6} placeholder="PIN Code *"
                      value={customer.pincode}
                      onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className={`${field} ${customer.pincode && !/^\d{6}$/.test(customer.pincode) ? "ring-1 ring-red-500" : ""}`}
                      autoComplete="postal-code" pattern="\d{6}" title="6-digit Indian PIN Code" />
                    <input data-testid="checkout-country" value="India" disabled readOnly
                      className={`${field} opacity-70 cursor-not-allowed`} />
                  </div>
                  {customer.pincode && !/^\d{6}$/.test(customer.pincode) && (
                    <p className="text-xs text-red-600" data-testid="pincode-error">Please enter a valid 6-digit Indian PIN Code.</p>
                  )}
                  {pinLookup.loading && (
                    <p className="text-xs text-muted-foreground" data-testid="pincode-lookup">Looking up city, district and state…</p>
                  )}
                  {pinLookup.error && /^\d{6}$/.test(customer.pincode) && (
                    <p className="text-xs text-amber-700" data-testid="pincode-lookup-error">{pinLookup.error}</p>
                  )}
                </div>
              </div>

              <div>
                <h2 className="font-display text-2xl mb-5">Payment method</h2>
                <RadioGroup value={gateway} onValueChange={setGateway} className="space-y-3" data-testid="gateway-group">
                  <label htmlFor="g-razorpay" className={`flex items-center gap-4 border rounded-2xl p-5 cursor-pointer transition-colors ${gateway === "razorpay" ? "border-black bg-[#F5F5F5]" : "border-black/15"} ${!config.razorpay_enabled ? "opacity-50" : ""}`}>
                    <RadioGroupItem value="razorpay" id="g-razorpay" data-testid="gateway-razorpay" disabled={!config.razorpay_enabled} />
                    <div className="flex-1">
                      <p className="text-sm uppercase tracking-[0.15em]">Razorpay</p>
                      <p className="text-xs text-muted-foreground mt-1">{config.razorpay_enabled ? "UPI · Cards · Netbanking · Wallets" : "Awaiting API keys — coming soon"}</p>
                    </div>
                  </label>
                  <label htmlFor="g-cod" className={`flex items-center gap-4 border rounded-2xl p-5 cursor-pointer transition-colors ${gateway === "cod" ? "border-black bg-[#F5F5F5]" : "border-black/15"}`}>
                    <RadioGroupItem value="cod" id="g-cod" data-testid="gateway-cod" />
                    <div className="flex-1">
                      <p className="text-sm uppercase tracking-[0.15em]">Cash on Delivery</p>
                      <p className="text-xs text-muted-foreground mt-1">Pay in cash when your order arrives. Phone & address required.</p>
                    </div>
                  </label>
                  <label htmlFor="g-stripe" className={`flex items-center gap-4 border rounded-2xl p-5 cursor-pointer transition-colors ${gateway === "stripe" ? "border-black bg-[#F5F5F5]" : "border-black/15"}`}>
                    <RadioGroupItem value="stripe" id="g-stripe" data-testid="gateway-stripe" />
                    <div className="flex-1">
                      <p className="text-sm uppercase tracking-[0.15em]">Card / Stripe</p>
                      <p className="text-xs text-muted-foreground mt-1">Secure hosted checkout · international cards</p>
                    </div>
                  </label>
                </RadioGroup>
                <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-2"><Lock size={12} /> Payments are encrypted and processed securely.</p>
              </div>
            </div>

            <div className="lg:col-span-4 lg:col-start-9">
              <div className="bg-[#F5F5F5] rounded-3xl p-8 sticky top-24" data-testid="order-summary">
                <h2 className="font-display text-2xl mb-6">Order summary</h2>
                <div className="space-y-4 mb-6">
                  {items.map((it) => (
                    <div key={it.slug} className="flex gap-3">
                      <div className="w-14 h-16 bg-white rounded-xl overflow-hidden shrink-0">
                        <img src={`${it.image}?w=200&q=70`} alt={it.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex justify-between">
                        <div>
                          <p className="font-display text-base leading-tight">{it.name}</p>
                          <p className="text-xs text-muted-foreground">Qty {it.quantity}</p>
                        </div>
                        <span className="text-sm">{formatINR(it.price * it.quantity)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex gap-2" data-testid="coupon-row">
                  <input data-testid="coupon-input" placeholder="Coupon code" value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    className="flex-1 bg-white rounded-full px-5 py-3 text-sm outline-none border border-black/10 focus:border-black transition" />
                  <button onClick={applyCoupon} data-testid="coupon-apply"
                    className="bg-black text-white rounded-full px-6 py-3 uppercase text-[11px] tracking-[0.15em]">Apply</button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Try WELCOME10 for 10% off.</p>
                <div className="border-t border-black/10 pt-5 mt-5 space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{formatINR(total)}</span></div>
                  {discount > 0 && (
                    <div className="flex justify-between text-black" data-testid="checkout-discount"><span>Discount ({applied.code})</span><span>−{formatINR(discount)}</span></div>
                  )}
                  <div className="flex justify-between text-muted-foreground"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
                  <div className="flex justify-between text-lg pt-3 border-t border-black/10">
                    <span className="uppercase tracking-widest">Total</span>
                    <span data-testid="checkout-total">{formatINR(grand)}</span>
                  </div>
                </div>
                <button onClick={pay} disabled={processing} data-testid="pay-now-btn"
                  className="w-full mt-6 bg-black text-white rounded-full py-4 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors disabled:opacity-60">
                  {processing ? "Processing…" : gateway === "cod" ? `Place COD Order · ${formatINR(grand)}` : `Pay ${formatINR(grand)}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
